// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useEffect, useState } from 'react';

import type { FolderContentsData, FolderData } from '@aps_sdk/data-management';
import { type ProjectId, type AutodeskCloudModel, useEvolvePaginatedGet } from '@evolvemep/api-ts';
import { Divider, Flex, Loader, Text, Card } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { useAutodeskAuth } from 'components/Autodesk/auth/useAutodeskAuth';
import { getAllDataFromFetcher } from 'helpers/getAllDataFromFetcher';
import { hasLengthAtLeast, hasLengthExactly } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useLocalStorage } from 'hooks/useLocalStorage';

import { AutodeskFolderBreadcrumb } from './AutodeskFolderBreadcrumb';
import { AutodeskProjectPicker } from './AutodeskProjectPicker';
import { ModelLinkAndPopover } from './ModelLinkAndPopover';

type Props = {
  projectId: ProjectId;
  refreshProjects: () => void;
};

export const AutodeskCloudBrowser = ({ projectId, refreshProjects }: Props) => {
  const { dataClient } = useAutodeskAuth();

  const [selectedHub, setSelectedHub] = useLocalStorage('AUTODESK_HUB', null);
  const [selectedAutodeskProject, setSelectedAutodeskProject] = useLocalStorage('AUTODESK_PROJECT', null);

  const { fetchPage: fetchMappedModels } = useEvolvePaginatedGet(`shop/autodesk/autodeskModel`);
  const [mappedModels, setMappedModels] = useState<AutodeskCloudModel['urn'][]>([]);

  useEffect(() => {
    if (isNotNil(selectedAutodeskProject)) {
      setMappedModels([]);
      void getAllDataFromFetcher(fetchMappedModels, {
        params: {
          projectId,
          autodeskProjectId: selectedAutodeskProject,
        },
      }).then((models) => setMappedModels(models.map((m) => m.urn)));
    }
  }, [fetchMappedModels, projectId, selectedAutodeskProject]);

  const [loading, setLoading] = useState(false);
  const [folderContents, setFolderContents] = useState<FolderContentsData[]>();

  const [folderPath, setFolderPath] = useState<FolderData[]>([]);

  const onFolderSelected = useCallback(
    (folder: FolderData) => {
      if (isNotNil(selectedAutodeskProject)) {
        setFolderContents(undefined);
        setLoading(true);
        setFolderPath((history) => {
          const index = history.findIndex((f) => f.id === folder.id);
          return [folder, ...history.slice(index + 1)];
        });
        void dataClient
          .getFolderContents(selectedAutodeskProject, folder.id)
          .then((c) => setFolderContents(c.data))
          .finally(() => setLoading(false));
      }
    },
    [dataClient, selectedAutodeskProject],
  );

  const getTopLevelFolders = useCallback(
    (autoSelectFirst = false) => {
      if (isNotNil(selectedHub) && isNotNil(selectedAutodeskProject)) {
        setFolderContents(undefined);
        setFolderPath([]);
        setLoading(true);
        void dataClient
          .getProjectTopFolders(selectedHub, selectedAutodeskProject)
          .then((f) => {
            if (isNotNil(f.data) && hasLengthExactly(f.data, 1) && autoSelectFirst) {
              onFolderSelected(f.data[0]);
            } else {
              setLoading(false);
              setFolderContents(f.data);
            }
          })
          .catch(() => setLoading(false));
      }
    },
    [dataClient, onFolderSelected, selectedAutodeskProject, selectedHub],
  );

  useEffect(() => {
    getTopLevelFolders(true);
  }, [getTopLevelFolders]);

  return (
    <>
      <Flex gap="xs" mb="lg">
        <AutodeskProjectPicker
          selectedAutodeskProject={selectedAutodeskProject}
          setSelectedAutodeskProject={setSelectedAutodeskProject}
          selectedHub={selectedHub}
          setSelectedHub={setSelectedHub}
        />
      </Flex>
      <Card withBorder h="100%">
        <Flex gap="xs" style={{ textWrap: 'nowrap', overflow: 'hidden' }} flex="0 0 auto">
          {isNil(selectedAutodeskProject) ? (
            <Text c="dimmed" fz="sm">
              Select an Autodesk Project.
            </Text>
          ) : (
            <AutodeskFolderBreadcrumb
              folderPath={folderPath}
              clearFolderPath={getTopLevelFolders}
              onFolderSelected={onFolderSelected}
            />
          )}
        </Flex>
        <Divider my="xs" />
        {loading && <Loader type="dots" />}
        {!loading && folderContents?.length === 0 && (
          <Text c="dimmed" m="xs" fz="sm">
            Nothing found.{' '}
            {hasLengthAtLeast(folderPath, 1) && (
              <Text span className="link" onClick={() => onFolderSelected(folderPath[0])}>
                Refresh?
              </Text>
            )}
          </Text>
        )}
        <Flex direction="column" flex="1 1 auto" style={{ overflowY: 'auto' }}>
          <Flex direction="column" gap="xs" flex="1 0 auto">
            {isNotNil(selectedHub) &&
              isNotNil(selectedAutodeskProject) &&
              folderContents?.map((c) =>
                c.type === 'items' ? (
                  <ModelLinkAndPopover
                    key={c.id}
                    model={c}
                    linked={mappedModels.includes(c.id as AutodeskCloudModel['urn'])}
                    selectedHub={selectedHub}
                    selectedAutodeskProject={selectedAutodeskProject}
                    projectId={projectId}
                    onModelLinked={(urn) => {
                      setMappedModels((mapped) => [...mapped, urn]);
                      refreshProjects();
                    }}
                    onModelUnlinked={(urn) => {
                      setMappedModels((mapped) => {
                        const index = mapped.indexOf(urn);
                        if (index < 0) return mapped;
                        return [...mapped.slice(0, index), ...mapped.slice(index + 1)];
                      });
                      refreshProjects();
                    }}
                  />
                ) : (
                  <Flex
                    className="link"
                    c="black"
                    key={c.id}
                    gap="xs"
                    style={{ overflow: 'hidden' }}
                    onClick={() => onFolderSelected(c)}
                  >
                    <Flex w={24} miw={24} justify="center" align="center">
                      <EvolveIcon size="sm" color="gray.9" icon="Folder" />
                    </Flex>
                    <Text
                      title={c.attributes.displayName}
                      style={{ textWrap: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
                    >
                      {c.attributes.displayName}
                    </Text>
                  </Flex>
                ),
              )}
          </Flex>
        </Flex>
      </Card>
    </>
  );
};
